import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Link from '@mui/material/Link';

import { getAccountUsage, getUsageTerm, getUsageTermOptions, UsageChartType, UsageTerm, UsageTermOption } from "../ApiUsagePage";
import { InfegyAccount } from "system/InfegyUser";
import UsageBarChart, { getNonEmptyUsageCategories, sortUsageCategories } from "routes/apiUsage/components/charts/UsageBarChart";
import { UsageDisplayMetric } from "../ApiUsagePage";
import { TextLoader } from "components/apiHandling/TextLoader";
import RollingUsageBarChart from "routes/apiUsage/components/charts/AccumulatedUsageBarChart";
import { Usage, UsageCategoryKey } from "../types/Usage";
import UsageCards from "../components/UsageCards";
import UsersUsageDataGrid from "routes/apiUsage/components/UsersUsageDataGrid";
import DateRangeLabel from "../components/DateRangeLabel";

interface AccountUsageProps {
  account: InfegyAccount;
  currentTerm: UsageTerm;
  onSelectUser: (userId: string) => void;
  visible: boolean;
}

export default function AccountUsage(props: AccountUsageProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const usageTermOptions = getUsageTermOptions(i18n);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [usageDisplayMetric, setUsageDisplayMetric] = useState<UsageDisplayMetric>("cost");
  const [selectedUsageTermOption, setSelectedUsageTermOption] = useState<UsageTermOption>(usageTermOptions[0]);
  const [activeUsageChart, setActiveUsageChart] = useState<UsageChartType>(UsageChartType.PeriodicUsage);

  const [accountUsage, setAccountUsage] = useState<Usage | null>(null);
  const [hasAnyUsage, setHasAnyUsage] = useState<boolean>(false);
  const [usageCategoryOptions, setUsageCategoryOptions] = useState<Array<UsageCategoryKey>>([]);
  const [selectedUsageCategories, _setSelectedUsageCategories] = useState<Array<UsageCategoryKey>>([]);

  function setSelectedUsageCategories(categories: Array<UsageCategoryKey>) {
    let sortedCategories = categories;
    if (accountUsage) {
      sortUsageCategories(categories, accountUsage, usageDisplayMetric, i18n);
    }
    _setSelectedUsageCategories(sortedCategories);
  }

  async function load() {
    setIsLoading(true);
    setLoadError("");
    setAccountUsage(null);
    setHasAnyUsage(false);
    setUsageCategoryOptions([]);
    setSelectedUsageCategories([]);
    
    try {
      const activeUsageTerm = getUsageTerm(selectedUsageTermOption.type, props.currentTerm);

      const usage = await getAccountUsage(activeUsageTerm, props.account.id);
      const usageBuckets = Object.keys(usage.total.count);
      const hasAnyUsage = !!usageBuckets.length;

      let usageCategoryOptions = getNonEmptyUsageCategories(usage, usageDisplayMetric);
      usageCategoryOptions = sortUsageCategories(usageCategoryOptions, usage, usageDisplayMetric, i18n);

      setUsageCategoryOptions(usageCategoryOptions);
      setSelectedUsageCategories(usageCategoryOptions);
      setAccountUsage(usage);
      setHasAnyUsage(hasAnyUsage);
    } catch (error: any) {
      if (error.code) {
        // This was an error from the API.  Display it as such.
        setLoadError(`${error.message}: ${error.server_message}`);
      } else {
        // This was an error from the UI.
        setLoadError(`${i18n.t("api-usage-page.error-loading")} ${error}`);
      }
    }

    setIsLoading(false);
  }

  useEffect(() => {
    load();
  }, [props.currentTerm, props.account, selectedUsageTermOption]);

  const tokens: Array<any> = accountUsage?.users?.[0]?.tokens || [];
  const hasApiToken = !!tokens.length;

  return (
    <Stack
      sx={{
        display: props.visible ? "auto" : "none",
        gap: 1
      }}>
      {isLoading &&
        <TextLoader />
      }
      {!isLoading &&
        <Box>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              justifyContent: "flex-end"
            }}>
            <Typography variant="h3" sx={{
              width: "100%"
            }}>{i18n.t("account-usage.account-usage-heading")}</Typography>
            {!loadError &&
              <>
                <ToggleButtonGroup
                  exclusive={true}
                  onChange={(event, value: UsageDisplayMetric) => value && setUsageDisplayMetric(value)}
                  value={usageDisplayMetric}>
                  <ToggleButton size="small" value="cost">{i18n.t("usage.metrics.cost")}</ToggleButton>
                  <ToggleButton size="small" value="count">{i18n.t("usage.metrics.count")}</ToggleButton>
                  <ToggleButton size="small" value="price">{i18n.t("usage.metrics.price")}</ToggleButton>
                </ToggleButtonGroup>
                <Autocomplete
                  disableClearable
                  getOptionLabel={(option) => option.label}
                  options={usageTermOptions}
                  onChange={(event, value) => setSelectedUsageTermOption(value) }
                  renderInput={(params) => <TextField autoComplete="off" {...params} />}
                  renderOption={(props, option, state) => {
                    const unkeyedProps: any = {...props};
                    delete unkeyedProps["key"];
                    return (
                      <li
                        key={props.key}
                        {...unkeyedProps}
                        style={{
                          ...props.style,
                          ...(option.type === selectedUsageTermOption.type && {backgroundColor: theme.colors.primary.light, fontWeight: "bold"})
                        }}
                      >
                        {option.label}
                      </li>
                    )
                  }}
                  size="small"
                  sx={{width: 400}}
                  value={selectedUsageTermOption}
                />
              </>
            }
          </Stack>
          {accountUsage &&
            <DateRangeLabel end={accountUsage.until} start={accountUsage.since} />
          }
        </Box>
      }
      {!isLoading && loadError &&
        <Alert severity="error" variant="outlined">{loadError}</Alert>
      }
      {!isLoading && !loadError &&
        <>
          
          {accountUsage &&
            <>
              {!hasAnyUsage &&
                <Alert severity="info" variant="outlined">{i18n.t("account-usage.no-api-usage-info")}</Alert>
              }
              {hasAnyUsage &&
                <>
                  <ToggleButtonGroup
                    exclusive={true}
                    onChange={(event, value: UsageChartType) => setActiveUsageChart(value)}
                    value={activeUsageChart}>
                    <ToggleButton size="small" value={UsageChartType.PeriodicUsage}>{i18n.t("account-usage.toggle-periodic-usage")}</ToggleButton>
                    <ToggleButton size="small" value={UsageChartType.AccumulatedUsage}>{i18n.t("account-usage.toggle-accumulated-usage")}</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{
                    display: activeUsageChart === UsageChartType.PeriodicUsage ? "auto" : "none"
                  }}>
                    <UsageBarChart 
                      metric={usageDisplayMetric}
                      onChange={(categories) => setSelectedUsageCategories(categories)}
                      selectedUsageCategories={selectedUsageCategories}
                      usage={accountUsage}
                      usageCategoryOptions={usageCategoryOptions}
                    />
                  </Box>
                  <Box sx={{
                    display: activeUsageChart === UsageChartType.AccumulatedUsage ? "auto" : "none"
                  }}>
                    <RollingUsageBarChart
                      metric={usageDisplayMetric}
                      onChange={(categories) => setSelectedUsageCategories(categories)}
                      selectedUsageCategories={selectedUsageCategories}
                      usage={accountUsage}
                      usageCategoryOptions={usageCategoryOptions}
                    />
                  </Box>
                </>
              }
              <Box><i>
                {i18n.t("account-usage.usage-endpoint-info")}&nbsp;
                <Link href={"https://starscape.infegy.com/api/docs/paths/usage/get"} target="_blank" rel="noreferrer" >
                  {i18n.t("account-usage.usage-endpoint-link")}
                </Link>
                .
              </i></Box>
              <Typography variant="h3">{i18n.t("account-usage.usage-categories-heading")}</Typography>
              <UsageCards metric={usageDisplayMetric} usage={accountUsage} />
              <Typography variant="h3">{i18n.t("account-usage.account-users-usage")}</Typography>
              {!hasApiToken &&
                <Alert severity="info" variant="outlined">{i18n.t("account-usage.no-user-usage-info")}</Alert>
              }
              {hasApiToken && props.visible &&
                <UsersUsageDataGrid
                  onClick={(userId) => props.onSelectUser(userId)}
                  usageMetric={usageDisplayMetric}
                  userUsage={accountUsage.users}
                />
              }
            </>
          }
        </>
      }
    </Stack>
  );
}